/**
 * OperationStatusBadge - Status badge for bulk create operations
 */

import { CheckCircle, Clock, Loader2, StopCircle, XCircle } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { ExternalTaskBulkCreateOperationStatus } from '@/services/api/generated/externalTaskService';

interface OperationStatusBadgeProps {
  status: ExternalTaskBulkCreateOperationStatus;
  className?: string;
}

const STATUS_CONFIG: Record<
  ExternalTaskBulkCreateOperationStatus,
  {
    color: 'zinc' | 'blue' | 'green' | 'red' | 'amber';
    icon: React.ElementType;
    label: string;
  }
> = {
  PENDING: { color: 'zinc', icon: Clock, label: 'Pending' },
  RUNNING: { color: 'blue', icon: Loader2, label: 'Running' },
  COMPLETED: { color: 'green', icon: CheckCircle, label: 'Completed' },
  FAILED: { color: 'red', icon: XCircle, label: 'Failed' },
  CANCELLED: { color: 'amber', icon: StopCircle, label: 'Cancelled' },
};

export const OperationStatusBadge: React.FC<OperationStatusBadgeProps> = ({
  status,
  className,
}) => {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const isAnimated = status === 'RUNNING';

  return (
    <Badge color={config.color} className={className}>
      <Icon
        className={`h-3.5 w-3.5 mr-1 ${isAnimated ? 'animate-spin' : ''}`}
      />
      {config.label}
    </Badge>
  );
};
